import type { ChartProps } from "@quillsql/react"
import { useEffect, useState } from "react"

import { Label } from "@atoms"

import { Select } from "@molecules"
import type { SelectListItem } from "@molecules/Select/SelectListItem/SelectListItem.types"

import styles from "./QuillMultiSelect.module.scss"

type MultiSelectComponentProps = Parameters<NonNullable<ChartProps["MultiSelectComponent"]>>[0]

function toStringSet(value: (string | number | null)[] | undefined): Set<string> {
    const values = (value ?? []).filter((entry): entry is string | number => entry != null).map(String)
    return new Set(values)
}

export default function QuillMultiSelect(props: MultiSelectComponentProps) {
    const { label, value, options, onChange, disabled } = props

    const selectOptions: SelectListItem[] = options.map((option) => ({
        label: option.label,
        value: String(option.value),
    }))

    const [selectedValues, setSelectedValues] = useState<Set<string>>(() => toStringSet(value))
    const [isOpen, setIsOpen] = useState(false)

    // Re-sync internal state when the controlled `value` prop changes (e.g. a parent-driven reset).
    useEffect(() => {
        setSelectedValues(toStringSet(value))
    }, [value])

    const allSelected = selectedValues.size === selectOptions.length

    const handleToggleItem = (item: SelectListItem) => {
        setSelectedValues((previous) => {
            const next = new Set(previous)
            if (next.has(item.value)) {
                next.delete(item.value)
            } else {
                next.add(item.value)
            }
            return next
        })
    }

    const handleToggleAll = () => {
        if (allSelected) {
            setSelectedValues(new Set())
        } else {
            setSelectedValues(new Set(selectOptions.map((option) => option.value)))
        }
    }

    const handleApply = () => {
        const selectedArray = Array.from(selectedValues)
        onChange({ target: { value: selectedArray } })
        setIsOpen(false)
    }

    const triggerLabel = selectedValues.size > 0 ? `${selectedValues.size} selected` : undefined

    return (
        <div className={styles.base}>
            {label && <Label label={label} variant="subtle" />}
            <Select.Root selectionMode="multi" isOpen={isOpen} onOpenChange={setIsOpen}>
                <Select.Input
                    isControlled={false}
                    placeholder="Select"
                    size="sm"
                    isDisabled={disabled}
                    selectedItem={triggerLabel ? { label: triggerLabel, value: "" } : undefined}
                />
                <Select.Viewport>
                    <Select.List title={label ?? "Select"}>
                        <Select.ListSearch />
                        <Select.ListGroup>
                            <Select.ListItem
                                variant="default"
                                item={{ label: "Select all", value: "__select_all__" }}
                                isSelected={allSelected}
                                onSelect={handleToggleAll}
                            />
                            {selectOptions.map((option) => (
                                <Select.ListItem
                                    key={option.value}
                                    variant="default"
                                    item={option}
                                    isSelected={selectedValues.has(option.value)}
                                    onSelect={handleToggleItem}
                                />
                            ))}
                        </Select.ListGroup>
                        <Select.ListFooter
                            variant="action-bar"
                            confirmButton={{
                                variant: "solid",
                                size: "lg",
                                onClick: handleApply,
                                children: "Apply",
                            }}
                        />
                    </Select.List>
                </Select.Viewport>
            </Select.Root>
        </div>
    )
}
